import { messageForErrorCode } from "./messages.js";
import type {
  FormerieClientConfig,
  PublicFormSchemaResponse,
  PublicFormSchemaResult,
  FormerieRuntimeClientConfig,
  SubmitErrorResponse,
  SubmitPayload,
  SubmitRequestOptions,
  SubmitResponse,
  ValidatePayload,
  ValidateResponse,
  ValidateSuccessResponse,
} from "./types.js";

export const FORMERIE_API_ENDPOINT = "https://api.formerie.com";
export const FORMERIE_RUNTIME_ENDPOINT = "https://forms.formerie.com";

export class FormerieClient {
  protected readonly endpoint: string;
  private readonly fetcher: typeof fetch;
  private readonly defaultHeaders?: HeadersInit;

  constructor(config: FormerieClientConfig = {}) {
    this.endpoint = normalizeEndpoint(config.endpoint ?? FORMERIE_API_ENDPOINT);
    this.fetcher = config.fetcher ?? globalThis.fetch.bind(globalThis);
    this.defaultHeaders = config.defaultHeaders;
  }

  submit(
    workspace: string,
    form: string,
    payload: SubmitPayload,
    options: SubmitRequestOptions = {},
  ): Promise<SubmitResponse> {
    return this.send(buildCanonicalSubmitUrl(this.endpoint, workspace, form), payload, options);
  }

  submitBranded(
    form: string,
    payload: SubmitPayload,
    options: SubmitRequestOptions = {},
  ): Promise<SubmitResponse> {
    return this.send(buildBrandedSubmitUrl(this.endpoint, form), payload, options);
  }

  validate(
    workspace: string,
    form: string,
    payload: ValidatePayload,
    options: SubmitRequestOptions = {},
  ): Promise<ValidateResponse> {
    return this.sendValidation(buildCanonicalValidateUrl(this.endpoint, workspace, form), payload, options);
  }

  validateBranded(
    form: string,
    payload: ValidatePayload,
    options: SubmitRequestOptions = {},
  ): Promise<ValidateResponse> {
    return this.sendValidation(buildBrandedValidateUrl(this.endpoint, form), payload, options);
  }

  schema(
    workspace: string,
    form: string,
    options: SubmitRequestOptions = {},
  ): Promise<PublicFormSchemaResult> {
    return this.get(buildCanonicalSchemaUrl(this.endpoint, workspace, form), options);
  }

  schemaBranded(
    form: string,
    options: SubmitRequestOptions = {},
  ): Promise<PublicFormSchemaResult> {
    return this.get(buildBrandedSchemaUrl(this.endpoint, form), options);
  }

  canonicalUrl(workspace: string, form: string): string {
    return buildCanonicalSubmitUrl(this.endpoint, workspace, form);
  }

  brandedUrl(form: string): string {
    return buildBrandedSubmitUrl(this.endpoint, form);
  }

  canonicalSchemaUrl(workspace: string, form: string): string {
    return buildCanonicalSchemaUrl(this.endpoint, workspace, form);
  }

  brandedSchemaUrl(form: string): string {
    return buildBrandedSchemaUrl(this.endpoint, form);
  }

  canonicalValidateUrl(workspace: string, form: string): string {
    return buildCanonicalValidateUrl(this.endpoint, workspace, form);
  }

  brandedValidateUrl(form: string): string {
    return buildBrandedValidateUrl(this.endpoint, form);
  }

  protected async send(
    url: string,
    payload: SubmitPayload,
    options: SubmitRequestOptions,
  ): Promise<SubmitResponse> {
    try {
      const response = await this.fetcher(url, {
        method: "POST",
        headers: mergeHeaders(this.defaultHeaders, options.headers, true),
        body: JSON.stringify(payload),
        signal: options.signal,
      });

      const body = await parseJsonResponse(response);
      return normalizeSubmitResponse(body);
    } catch {
      return {
        ok: false,
        error: {
          code: "NETWORK_ERROR",
          message: messageForErrorCode("NETWORK_ERROR"),
        },
      };
    }
  }

  protected async sendValidation(
    url: string,
    payload: ValidatePayload,
    options: SubmitRequestOptions,
  ): Promise<ValidateResponse> {
    try {
      const response = await this.fetcher(url, {
        method: "POST",
        headers: mergeHeaders(this.defaultHeaders, options.headers, true),
        body: JSON.stringify(payload),
        signal: options.signal,
      });

      const body = await parseJsonResponse(response);
      return normalizeValidateResponse(body);
    } catch {
      return {
        ok: false,
        error: {
          code: "NETWORK_ERROR",
          message: messageForErrorCode("NETWORK_ERROR"),
        },
      };
    }
  }

  protected async get(
    url: string,
    options: SubmitRequestOptions,
  ): Promise<PublicFormSchemaResult> {
    try {
      const response = await this.fetcher(url, {
        method: "GET",
        headers: mergeHeaders(this.defaultHeaders, options.headers, false),
        signal: options.signal,
      });

      const body = await parseJsonResponse(response);
      return normalizeSchemaResponse(body);
    } catch {
      return {
        ok: false,
        error: {
          code: "NETWORK_ERROR",
          message: messageForErrorCode("NETWORK_ERROR"),
        },
      };
    }
  }
}

export function buildCanonicalSubmitUrl(endpoint: string, workspace: string, form: string): string {
  return `${normalizeEndpoint(endpoint)}/v1/${encodePathSegment(workspace)}/${encodePathSegment(form)}`;
}

export function buildBrandedSubmitUrl(endpoint: string, form: string): string {
  return `${normalizeEndpoint(endpoint)}/v1/${encodePathSegment(form)}`;
}

export function buildCanonicalSchemaUrl(endpoint: string, workspace: string, form: string): string {
  return `${buildCanonicalSubmitUrl(endpoint, workspace, form)}/schema`;
}

export function buildBrandedSchemaUrl(endpoint: string, form: string): string {
  return `${buildBrandedSubmitUrl(endpoint, form)}/schema`;
}

export function buildCanonicalValidateUrl(endpoint: string, workspace: string, form: string): string {
  return `${buildCanonicalSubmitUrl(endpoint, workspace, form)}/validate`;
}

export function buildBrandedValidateUrl(endpoint: string, form: string): string {
  return `${buildBrandedSubmitUrl(endpoint, form)}/validate`;
}

function normalizeEndpoint(endpoint: string): string {
  return endpoint.replace(/\/+$/, "");
}

function encodePathSegment(segment: string): string {
  return encodeURIComponent(segment.trim());
}

function publicWorkspaceHandle(workspace: string): string {
  return workspace.trim().toLowerCase().split(".")[0] ?? "";
}

function mergeHeaders(
  defaultHeaders?: HeadersInit,
  headers?: HeadersInit,
  includeContentType = true,
): Headers {
  const merged = new Headers(defaultHeaders);
  merged.set("Accept", "application/json");

  if (includeContentType) {
    merged.set("Content-Type", "application/json");
  }

  if (headers) {
    new Headers(headers).forEach((value, key) => {
      merged.set(key, value);
    });
  }

  return merged;
}

async function parseJsonResponse(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function normalizeSubmitResponse(value: unknown): SubmitResponse {
  if (isSubmitSuccessShape(value)) {
    return value;
  }

  if (isRuntimeSubmitSuccessShape(value)) {
    return {
      ...value,
      submissionId: value.submissionUid,
    };
  }

  if (isSubmitErrorShape(value)) {
    return value;
  }

  return {
    ok: false,
    error: {
      code: "INVALID_RESPONSE",
      message: messageForErrorCode("INVALID_RESPONSE"),
    },
  };
}

function normalizeSchemaResponse(value: unknown): PublicFormSchemaResult {
  if (isSchemaSuccessShape(value)) {
    return value;
  }

  if (isSubmitErrorShape(value)) {
    return value;
  }

  return {
    ok: false,
    error: {
      code: "INVALID_RESPONSE",
      message: messageForErrorCode("INVALID_RESPONSE"),
    },
  };
}

function normalizeValidateResponse(value: unknown): ValidateResponse {
  if (isValidateSuccessShape(value)) {
    return value;
  }

  if (isSubmitErrorShape(value)) {
    return value;
  }

  return {
    ok: false,
    error: {
      code: "INVALID_RESPONSE",
      message: messageForErrorCode("INVALID_RESPONSE"),
    },
  };
}

function isSubmitSuccessShape(value: unknown): value is SubmitResponse {
  if (!isRecord(value)) {
    return false;
  }

  return value.ok === true && typeof value.submissionId === "string" && value.status === "accepted";
}

function isRuntimeSubmitSuccessShape(value: unknown): value is { ok: true; status: "accepted"; submissionUid: string } {
  if (!isRecord(value)) {
    return false;
  }

  return value.ok === true && typeof value.submissionUid === "string" && value.status === "accepted";
}

function isValidateSuccessShape(value: unknown): value is ValidateSuccessResponse {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.ok === true &&
    value.status === "valid" &&
    (value.mode === "partial" || value.mode === "step") &&
    isRecord(value.values)
  );
}

function isSubmitErrorShape(value: unknown): value is SubmitErrorResponse {
  if (!isRecord(value) || value.ok !== false || !isRecord(value.error)) {
    return false;
  }

  if (typeof value.error.code !== "string" || typeof value.error.message !== "string") {
    return false;
  }

  if (value.error.code !== "VALIDATION_FAILED") {
    return true;
  }

  return isRecord(value.error.fields) && Array.isArray(value.error.issues);
}

function isSchemaSuccessShape(value: unknown): value is PublicFormSchemaResponse {
  if (!isRecord(value) || value.ok !== true) {
    return false;
  }

  return (
    typeof value.enabled === "boolean" &&
    typeof value.formHandle === "string" &&
    typeof value.schemaId === "string" &&
    isRecord(value.turnstile) &&
    typeof value.turnstile.required === "boolean" &&
    isRecord(value.fields) &&
    typeof value.workspaceHandle === "string"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class FormServiceClient extends FormerieClient {}

export class FormerieRuntimeClient extends FormerieClient {
  constructor(config: FormerieRuntimeClientConfig = {}) {
    super({
      ...config,
      endpoint: config.endpoint ?? FORMERIE_RUNTIME_ENDPOINT,
    });
  }

  override submit(
    workspace: string,
    form: string,
    payload: SubmitPayload,
    options: SubmitRequestOptions = {},
  ): Promise<SubmitResponse> {
    return this.send(buildRuntimeSubmitUrl(this.endpoint, workspace, form), payload, options);
  }

  submitWorkspace(
    form: string,
    payload: SubmitPayload,
    options: SubmitRequestOptions = {},
  ): Promise<SubmitResponse> {
    return this.send(buildWorkspaceRuntimeSubmitUrl(this.endpoint, form), payload, options);
  }

  override submitBranded(
    form: string,
    payload: SubmitPayload,
    options: SubmitRequestOptions = {},
  ): Promise<SubmitResponse> {
    return this.submitWorkspace(form, payload, options);
  }

  submitForm(
    payload: SubmitPayload,
    options: SubmitRequestOptions = {},
  ): Promise<SubmitResponse> {
    return this.send(buildFormRuntimeSubmitUrl(this.endpoint), payload, options);
  }

  override validate(
    workspace: string,
    form: string,
    payload: ValidatePayload,
    options: SubmitRequestOptions = {},
  ): Promise<ValidateResponse> {
    return this.sendValidation(buildRuntimeValidateUrl(this.endpoint, workspace, form), payload, options);
  }

  validateWorkspace(
    form: string,
    payload: ValidatePayload,
    options: SubmitRequestOptions = {},
  ): Promise<ValidateResponse> {
    return this.sendValidation(buildWorkspaceRuntimeValidateUrl(this.endpoint, form), payload, options);
  }

  override validateBranded(
    form: string,
    payload: ValidatePayload,
    options: SubmitRequestOptions = {},
  ): Promise<ValidateResponse> {
    return this.validateWorkspace(form, payload, options);
  }

  validateForm(
    payload: ValidatePayload,
    options: SubmitRequestOptions = {},
  ): Promise<ValidateResponse> {
    return this.sendValidation(buildFormRuntimeValidateUrl(this.endpoint), payload, options);
  }

  override schema(
    workspace: string,
    form: string,
    options: SubmitRequestOptions = {},
  ): Promise<PublicFormSchemaResult> {
    return this.get(buildRuntimeSchemaUrl(this.endpoint, workspace, form), options);
  }

  schemaWorkspace(
    form: string,
    options: SubmitRequestOptions = {},
  ): Promise<PublicFormSchemaResult> {
    return this.get(buildWorkspaceRuntimeSchemaUrl(this.endpoint, form), options);
  }

  override schemaBranded(
    form: string,
    options: SubmitRequestOptions = {},
  ): Promise<PublicFormSchemaResult> {
    return this.schemaWorkspace(form, options);
  }

  schemaForm(options: SubmitRequestOptions = {}): Promise<PublicFormSchemaResult> {
    return this.get(buildFormRuntimeSchemaUrl(this.endpoint), options);
  }

  override canonicalUrl(workspace: string, form: string): string {
    return buildRuntimeSubmitUrl(this.endpoint, workspace, form);
  }

  override brandedUrl(form: string): string {
    return buildWorkspaceRuntimeSubmitUrl(this.endpoint, form);
  }

  override canonicalSchemaUrl(workspace: string, form: string): string {
    return buildRuntimeSchemaUrl(this.endpoint, workspace, form);
  }

  override brandedSchemaUrl(form: string): string {
    return buildWorkspaceRuntimeSchemaUrl(this.endpoint, form);
  }

  override canonicalValidateUrl(workspace: string, form: string): string {
    return buildRuntimeValidateUrl(this.endpoint, workspace, form);
  }

  override brandedValidateUrl(form: string): string {
    return buildWorkspaceRuntimeValidateUrl(this.endpoint, form);
  }

  hostedUrl(workspace: string, form: string): string {
    return buildRuntimeHostedFormUrl(this.endpoint, workspace, form);
  }

  hostedWorkspaceUrl(form: string): string {
    return buildWorkspaceRuntimeHostedFormUrl(this.endpoint, form);
  }

  hostedFormUrl(): string {
    return buildFormRuntimeHostedFormUrl(this.endpoint);
  }

  embedScriptUrl(): string {
    return buildRuntimeEmbedScriptUrl(this.endpoint);
  }
}

export function buildRuntimeHostedFormUrl(endpoint: string, workspace: string, form: string): string {
  return `${normalizeEndpoint(endpoint)}/${encodePathSegment(publicWorkspaceHandle(workspace))}/${encodePathSegment(form)}`;
}

export function buildWorkspaceRuntimeHostedFormUrl(endpoint: string, form: string): string {
  return `${normalizeEndpoint(endpoint)}/${encodePathSegment(form)}`;
}

export function buildFormRuntimeHostedFormUrl(endpoint: string): string {
  return `${normalizeEndpoint(endpoint)}/`;
}

export function buildRuntimeEmbedScriptUrl(endpoint: string): string {
  return `${normalizeEndpoint(endpoint)}/embed.js`;
}

export function buildRuntimeSubmitUrl(endpoint: string, workspace: string, form: string): string {
  return `${buildRuntimeHostedFormUrl(endpoint, workspace, form)}/submit`;
}

export function buildWorkspaceRuntimeSubmitUrl(endpoint: string, form: string): string {
  return `${buildWorkspaceRuntimeHostedFormUrl(endpoint, form)}/submit`;
}

export function buildFormRuntimeSubmitUrl(endpoint: string): string {
  return `${normalizeEndpoint(endpoint)}/submit`;
}

export function buildRuntimeSchemaUrl(endpoint: string, workspace: string, form: string): string {
  return `${buildRuntimeHostedFormUrl(endpoint, workspace, form)}/schema`;
}

export function buildWorkspaceRuntimeSchemaUrl(endpoint: string, form: string): string {
  return `${buildWorkspaceRuntimeHostedFormUrl(endpoint, form)}/schema`;
}

export function buildFormRuntimeSchemaUrl(endpoint: string): string {
  return `${normalizeEndpoint(endpoint)}/schema`;
}

export function buildRuntimeValidateUrl(endpoint: string, workspace: string, form: string): string {
  return `${buildRuntimeHostedFormUrl(endpoint, workspace, form)}/validate`;
}

export function buildWorkspaceRuntimeValidateUrl(endpoint: string, form: string): string {
  return `${normalizeEndpoint(endpoint)}/${encodePathSegment(form)}/validate`;
}

export function buildFormRuntimeValidateUrl(endpoint: string): string {
  return `${normalizeEndpoint(endpoint)}/validate`;
}
